import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Tea Society Promotion Component
 * 
 * Calculates and displays dynamic savings based on cart total and discount percentage.
 * Replaces [savings] and [savings %] placeholders in the message.
 * 
 * @extends {Component}
 */
class TeaSocietyPromotionComponent extends Component {
  /** @type {string | null} */
  #messageTemplate = null;

  static get observedAttributes() {
    return ['discount-percentage', 'data-cart-grand-total'];
  }

  /**
   * Called when the element is connected to the document's DOM.
   */
  connectedCallback() {
    this.classList.add('calculating');
    super.connectedCallback();
    this.#calculateAndUpdateSavings();
  }
  /**
   * Called when the element is disconnected from the document's DOM.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Updates the cart total and recalculates savings.
   * Called externally by cart-items-component after cart updates.
   * @param {number} cartTotal - The cart total in cents.
   */
  updateCartTotal(cartTotal) {
    if (typeof cartTotal === 'number') {
      this.dataset.cartGrandTotal = cartTotal.toString();
      this.#calculateAndUpdateSavings();
    }
  }

  /**
   * Updates all tea-society-promotion-component instances with the latest cart total.
   * Called externally by cart-items-component after cart updates.
   * @static
   * @param {number} cartTotal - The cart total in cents.
   */
  static updateAllInstances(cartTotal) {
    if (typeof cartTotal !== 'number') return;

    const components = document.querySelectorAll('tea-society-promotion-component');
    
    for (const component of components) {
      if (component instanceof TeaSocietyPromotionComponent) {
        component.updateCartTotal(cartTotal);
      }
    }
  }

  /**
   * Calculates savings and updates the message with formatted values.
   */
  #calculateAndUpdateSavings() {
    const messageElement = this.refs.message;
    if (!(messageElement instanceof HTMLElement)) {
      this.classList.remove('calculating');
      return;
    }

    const discountPercentage = parseFloat(this.getAttribute('discount-percentage') || '10');
    let cartGrandTotal = parseInt(this.dataset.cartGrandTotal || '0', 10);
    
    if (cartGrandTotal <= 0) {
      this.classList.remove('calculating');
      return;
    }

    // Check if there's a gift wrap item in cart-form (gift wrap is free)
    const cartForm = document.getElementById('cart-form');
    const giftWrapPricingElement = cartForm?.querySelector('[data-giftwrap-original-price]');
    const giftWrapPriceInCents = giftWrapPricingElement 
      ? parseInt(giftWrapPricingElement.getAttribute('data-giftwrap-original-price') || '0', 10)
      : 0;
    
    let adjustedCartGrandTotal = cartGrandTotal;
    let giftWrapSavings = 0;
    
    if (giftWrapPriceInCents) {
      // Gift wrap is free, so subtract gift wrap price from cartGrandTotal and add to savings
      adjustedCartGrandTotal = Math.max(0, cartGrandTotal - giftWrapPriceInCents);
      giftWrapSavings = giftWrapPriceInCents;
    }

    const messageTemplate = this.#messageTemplate ?? messageElement.innerHTML;
    this.#messageTemplate = messageTemplate;

    // Calculate savings: adjustedGrandTotal * discountPercentage / 100
    // Plus gift wrap savings if applicable
    const discountSavings = Math.round((adjustedCartGrandTotal * discountPercentage) / 100);
    const savingsAmount = discountSavings + giftWrapSavings;
    
    // Format savings as money
    const formattedSavings = this.#formatMoney(savingsAmount);
    
    // Get the message content
    let messageContent = messageTemplate;
    
    // Replace [savings] placeholder with formatted money
    messageContent = messageContent.replace(/\[savings\]/g, formattedSavings);
    
    // Replace [savings %] placeholder with discount percentage
    messageContent = messageContent.replace(/\[savings %\]/g, `${discountPercentage}%`);
    
    // Update the message element
    messageElement.innerHTML = messageContent;
    
    // Remove calculating class after calculation completes
    this.classList.remove('calculating');
  }

  /**
   * Formats a price in cents to a money string.
   * Uses Shopify's money format template if available, otherwise falls back to basic formatting.
   * @param {number} priceInCents - The price in cents.
   * @returns {string} Formatted money string.
   */
  #formatMoney(priceInCents) {
    const win = /** @type {Window & {Shopify?: {formatMoney?: (amount: number) => string; money_format?: string}}} */ (window);
    
    // Try to use Shopify.formatMoney if available
    if (win.Shopify?.formatMoney) {
      return win.Shopify.formatMoney(priceInCents);
    }
    
    // Try to use money format template from shop
    const moneyFormat = this.dataset.moneyFormat || win.Shopify?.money_format;
    if (moneyFormat) {
      const amount = (priceInCents / 100).toFixed(2);
      return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
    }
    
    // Fallback: basic formatting
    const price = (priceInCents / 100).toFixed(2);
    const currencyMeta = document.querySelector('meta[name="shopify-currency"]');
    const currencyCode = currencyMeta?.getAttribute('content') || win.Shopify?.currency?.active || '$';
    
    // Basic formatting based on currency
    if (currencyCode === 'USD' || currencyCode === '$') {
      return `$${price}`;
    }
    
    return `${currencyCode} ${price}`;
  }
}

if (!customElements.get('tea-society-promotion-component')) {
  customElements.define('tea-society-promotion-component', TeaSocietyPromotionComponent);
}

